import React from 'react';
import { Avatar, StatusPill, Progress, IconBtn, AlertChip, Menu } from '../ui';
import { formatMoneyCompact, formatPercent } from '../../lib/format';
import {
  getInitials,
  statusToPill,
  alertText,
  managerLabel,
} from './projectUtils';

/**
 * Tile-view project card. Replaces the legacy `.card.project-card` markup
 * in ProjectList for the redesigned dashboard.
 *
 * Layout (per handoff README §2d):
 *   ┌─────────────────────────────────────────────────┐
 *   │ [Av]  Project name             ⦿ Active   ⋯     │
 *   │       #CODE                                     │
 *   │                                                 │
 *   │  Client        PM                               │
 *   │  Acme Corp     Johnny Brewer                    │
 *   │  Contract      Billed                           │
 *   │  $5.5M         $5.3M +$221k ret                 │
 *   │                                                 │
 *   │  Billed progress                      96.7%     │
 *   │  ████████████████████████████████░               │
 *   │                                                 │
 *   │  ⚠ 2 tasks behind schedule (1 critical)         │
 *   │  ⚠ 2 scheduling issues                          │
 *   └─────────────────────────────────────────────────┘
 *
 * The whole card is clickable → onSelect(project). The kebab uses
 * stopPropagation so menu clicks don't navigate.
 *
 * Step #7 will replace the bare kebab handler with a proper popover
 * menu (View / Edit / Archive / Delete). For now `onKebab` is whatever
 * the parent wants to do — typically open delete-confirm.
 */
export default function ProjectCard({
  project,
  alert,        // { alert_count, critical_count } from projectAlerts[id]
  depAlert,     // { alert_count, critical_count } from depAlerts[id]
  onSelect,
  onDelete,
  isDeleting = false,
}) {
  const contractVal =
    parseFloat(project.revised_contract_value) ||
    parseFloat(project.contract_amount) ||
    0;
  const billedVal = parseFloat(project.total_billed) || 0;
  const retainage = parseFloat(project.total_retainage) || 0;
  const billedPct = contractVal > 0 ? (billedVal / contractVal) * 100 : 0;

  const initials = getInitials(project);
  const pill = statusToPill(project.status);
  const progressColor = billedPct >= 95 ? 'var(--success)' : 'var(--accent)';

  return (
    <div
      onClick={() => onSelect && onSelect(project)}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 10,
        padding: 14,
        boxShadow: 'var(--shadow-sm)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        cursor: 'pointer',
        transition: 'border-color .12s, box-shadow .12s',
        minWidth: 0,
        opacity: isDeleting ? 0.55 : 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-strong)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--border-default)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {project.logo_url ? (
          <img
            src={project.logo_url}
            alt=""
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              objectFit: 'cover',
              border: '1px solid var(--border-subtle)',
              flexShrink: 0,
            }}
          />
        ) : (
          <Avatar initials={initials} size={34} accent />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 15.5,
              fontWeight: 600,
              color: 'var(--text-1)',
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={project.project_name}
          >
            {project.project_name}
          </div>
          {project.project_number && (
            <div
              className="mono"
              style={{
                fontSize: 12,
                color: 'var(--text-3)',
                marginTop: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              #{project.project_number}
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            flexShrink: 0,
          }}
        >
          <StatusPill status={pill.status} label={pill.label} />
          <Menu
            trigger={(toggle) => (
              <IconBtn
                name="kebab"
                label="More actions"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(e);
                }}
              />
            )}
            items={[
              {
                label: 'View details',
                onClick: () => onSelect && onSelect(project),
              },
              { divider: true },
              {
                label: 'Delete project',
                danger: true,
                onClick: () => onDelete && onDelete(project),
              },
            ]}
          />
        </div>
      </div>

      {/* Detail grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          rowGap: 8,
          columnGap: 12,
        }}
      >
        <DetailItem label="Client" value={project.client_name || '—'} />
        <DetailItem
          label={managerLabel(project.project_manager)}
          value={project.project_manager || '—'}
        />
        <DetailItem
          label="Contract"
          value={formatMoneyCompact(contractVal)}
          mono
        />
        <DetailItem
          label="Billed"
          value={formatMoneyCompact(billedVal)}
          mono
          suffix={
            retainage > 0 ? (
              <span
                className="mono"
                style={{
                  fontSize: 12.5,
                  color: 'var(--warning)',
                  fontWeight: 500,
                }}
              >
                {' +'}
                {formatMoneyCompact(retainage)} ret
              </span>
            ) : null
          }
        />
      </div>

      {/* Progress */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
            Billed progress
          </span>
          <span
            className="mono"
            style={{
              fontSize: 13.5,
              color: 'var(--text-1)',
              fontWeight: 500,
            }}
          >
            {formatPercent(billedPct)}
          </span>
        </div>
        <Progress value={billedPct} height={5} color={progressColor} />
      </div>

      {/* Alert chips — only when present */}
      {(alert || depAlert) && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}
        >
          {alert && (
            <AlertChip
              kind={alert.critical_count > 0 ? 'critical' : 'warning'}
              text={alertText(
                alert.alert_count,
                alert.critical_count,
                'task',
                'behind schedule'
              )}
            />
          )}
          {depAlert && (
            <AlertChip
              kind={depAlert.critical_count > 0 ? 'critical' : 'warning'}
              text={alertText(
                depAlert.alert_count,
                depAlert.critical_count,
                'scheduling issue',
                ''
              )}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------- helpers -------------------- */

function DetailItem({ label, value, mono = false, suffix = null }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontSize: 12,
          color: 'var(--text-3)',
          fontWeight: 500,
          letterSpacing: '0.01em',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        className={mono ? 'mono' : undefined}
        style={{
          fontSize: 14,
          color: 'var(--text-1)',
          fontWeight: 500,
          letterSpacing: mono ? '-0.02em' : undefined,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
        {suffix}
      </div>
    </div>
  );
}

/* Helpers (getInitials, statusToPill, alertText, managerLabel) live in
   ./projectUtils so ProjectTable can share them. */
